import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import {
  fetchWalletSummary,
  fetchConnectStatus,
  startOnboarding,
  getStripeDashboardLink,
} from "../../../../redux/specialist-slices/wallet.slice"
import OnboardingBanner from "./components/OnboardingBanner"
import WalletSummaryCards from "./components/WalletSummaryCards"
import TransactionsTab from "./components/TransactionsTab"
import PayoutsTab from "./components/PayoutsTab"
import { Box, Button, CircularProgress, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material"
import { formatPrice } from "../../../../utils/helper"

// const TABS = ["overview", "transactions", "payouts"]
const TABS = ["transactions", "payouts"]


function WalletPage() {
  const dispatch = useDispatch()
  const { search } = useLocation()
  const [activeTab, setActiveTab] = useState("transactions")
  // State to control visibility of compulsory Stripe connection dialog
  const [showConnectPopup, setShowConnectPopup] = useState(false)
  // Track whether we've done the initial load so we don't re-fetch on every render
  const initialized = useRef(false)

  // Show Stripe connection popup dialog if redirected from SpecialistProtected route guard
  useEffect(() => {
    const params = new URLSearchParams(search)
    if (params.get("stripe_connect_required") === "true") {
      setShowConnectPopup(true)
    }
  }, [search])

  const {
    wallet,
    recentTransactions,
    summaryLoading,
    summaryError,
    connectStatus,
    connectLoading,
    connectError,
    onboardingLoading,
  } = useSelector((s) => s.walletSlice)


  // On mount – fetch connect status + wallet summary in parallel.
  // Also re-fetch if returning from Stripe onboarding (?from=onboarding).
  useEffect(() => {
    const fromOnboarding = new URLSearchParams(search).get("from") === "onboarding"
    if (!initialized.current || fromOnboarding) {
      initialized.current = true
      dispatch(fetchConnectStatus())
      dispatch(fetchWalletSummary())
    }
  }, [dispatch, search])

  // Guard: prevent spamming the onboarding endpoint
  const handleStartOnboarding = async () => {
    if (onboardingLoading) return
    const action = await dispatch(startOnboarding())
    if (startOnboarding.fulfilled.match(action)) {
      const { redirectUrl } = action.payload
      if (redirectUrl) window.location.href = redirectUrl
    }
  }

  const handleStripeDashboard = async () => {
    const action = await dispatch(getStripeDashboardLink());

    if (getStripeDashboardLink.fulfilled.match(action)) {
      const  {url} = action.payload; 
      if (url) {
        window.open(url, "_blank");
      }
    }
  };
  return (
    <Box>
      {/* Page title */}
      <Typography
        variant="h5"
        fontWeight={700}
        color="other.color1"
        mb={3}
        sx={{ letterSpacing: "-0.3px" }}
      >
        Earnings &amp; Payouts
      </Typography>

      {/* ── Onboarding Banner ── */}
      <OnboardingBanner
        connectStatus={connectStatus}
        connectLoading={connectLoading}
        connectError={connectError}
        onboardingLoading={onboardingLoading}
        onStart={handleStartOnboarding}
      />

      {/* ── Wallet Summary Cards ── */}
      <WalletSummaryCards
        wallet={wallet}
        loading={summaryLoading}
        error={summaryError}
        onboardingComplete={connectStatus?.onboardingComplete}
      />

      {/* Stripe Account Card */}
      <Box
        sx={{
          mt: 2,
          p: 2.5,
          borderRadius: "14px",
          border: "1px solid #e5e7eb",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700}>
            Stripe Express Account
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Manage bank account, payouts, tax information and account verification.
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleStripeDashboard}
          disabled={!connectStatus?.onboardingComplete}
          sx={{
            whiteSpace: "nowrap",
            alignSelf: { xs: "stretch", sm: "auto" },
            height: 40,
          }}
        >
          Open Dashboard
        </Button>
      </Box>
      <Box
        sx={{
          mt: 2,
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          bgcolor: "#f8fafc",
          border: "1px solid #e2e8f0",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          ℹ️ Earnings from completed appointments are released within 24 hours after session completion, subject to applicable platform commission deductions.
        </Typography>
      </Box>

      {/* ── Tab Bar ── */}
      <Paper
        sx={{
          display: "flex",
          gap: 0.5,
          p: 0.75,
          mb: 3,
          mt: 3,
          borderRadius: "10px",
          bgcolor: "#f5f5f5",
          boxShadow: "none",
          border: "1px solid #ececec",
        }}
      >
        {TABS.map((tab) => (
          <Button
            key={tab}
            disableRipple
            onClick={() => setActiveTab(tab)}
            sx={{
              flex: 1,
              py: 1,
              borderRadius: "8px",
              textTransform: "capitalize",
              fontWeight: activeTab === tab ? 700 : 400,
              fontSize: "14px",
              color: activeTab === tab ? "white" : "other.color2",
              bgcolor:
                activeTab === tab ? "primary.main" : "transparent",
              "&:hover": {
                bgcolor:
                  activeTab === tab ? "primary.main" : "#ececec",
              },
              transition: "all 0.2s ease",
            }}
          >
            {tab}
          </Button>
        ))}
      </Paper>

      {/* ── Tab Content ── */}
      {activeTab === "overview" && (
        <RecentTransactionsInline
          transactions={recentTransactions}
          loading={summaryLoading}
          onboardingComplete={connectStatus?.onboardingComplete}
        />
      )}
      {activeTab === "transactions" && <TransactionsTab onboardingComplete={connectStatus?.onboardingComplete} />}
      {activeTab === "payouts" && <PayoutsTab onboardingComplete={connectStatus?.onboardingComplete} />}

      {/* ── Compulsory Stripe Connect Popup Dialog ── */}
      <Dialog
        open={showConnectPopup}
        onClose={() => setShowConnectPopup(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiBackdrop-root": {
            opacity: "0.5 !important",
            backgroundColor: "#00000080",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            Stripe Account Required
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography textAlign="center" variant="body1" sx={{ mt: 1 }}>
            Please connect your Stripe account to receive earnings from your sessions.
            Connecting Stripe is compulsory to receive bookings and receive payouts.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3, pt: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowConnectPopup(false);
              handleStartOnboarding();
            }}
            sx={{ px: 4, borderRadius: "8px", fontWeight: 600 }}
          >
            Connect Stripe Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Recent Transactions (overview tab – pulls from summary data)
// ─────────────────────────────────────────────────────────────────────────────
function RecentTransactionsInline({ transactions, loading, onboardingComplete }) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (!onboardingComplete) {
    return null
  }

  if (!transactions || transactions.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          color: "text.secondary",
          borderRadius: "12px",
          border: "1.5px dashed #ddd",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No recent transactions found.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography fontWeight={600} mb={2} color="other.color1">
        Recent Transactions
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {transactions.map((tx, i) => (
          <TransactionRow key={tx._id || i} tx={tx} />
        ))}
      </Box>
    </Box>
  )
}

function TransactionRow({ tx }) {
  // Valid WalletTransaction types per backend enum
  const typeColors = {
    credit_pending: { bg: "#fff8e1", color: "#f59e0b", label: "Pending Credit" },
    release_to_available: { bg: "#e8f5e9", color: "#22c55e", label: "Released" },
    payout: { bg: "#e3f2fd", color: "#3b82f6", label: "Payout" },
    reversal: { bg: "#fce4ec", color: "#ef4444", label: "Reversal" },
  }

  const meta = typeColors[tx.type] || { bg: "#f5f5f5", color: "#666", label: tx.type || "Unknown" }
  // Debit = money leaving specialist's balance
  const isDebit = tx.type === "payout" || tx.type === "reversal"

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        borderRadius: "10px",
        border: "1px solid #efefef",
        bgcolor: "#fafafa",
        "&:hover": { bgcolor: "#f5f5f5" },
        transition: "background 0.15s",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            bgcolor: meta.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
          }}
        >
          {isDebit ? "↓" : "↑"}
        </Box>
        <Box>
          <Typography fontWeight={600} fontSize="14px" color="other.color1">
            {meta.label}
          </Typography>
          <Typography fontSize="12px" color="text.secondary">
            {tx.note || "—"}
          </Typography>
          <Typography fontSize="11px" color="text.disabled" mt={0.25}>
            {tx.createdAt
              ? new Date(tx.createdAt).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
              : ""}
          </Typography>
        </Box>
      </Box>

      <Typography
        fontWeight={700}
        fontSize="15px"
        color={isDebit ? "#ef4444" : "#22c55e"}
      >
        {isDebit ? "−" : "+"}{formatPrice((tx.amount || 0) / 100, tx.currency || "inr")}
      </Typography>
    </Box>
  )
}

export { TransactionRow }
export default WalletPage
